import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Send,
  Mail,
  Phone,
  Loader2,
  CheckCircle2,
  Handshake,
  Shield,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../utils/errorFormatter";
import SEO from "../components/SEO";
import { PublicPageCopy } from "../components/PublicPageCopy";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  inquiry_type: "consultant",
  message: "",
};

export default function PartnershipPage() {
  const [form, setForm] = useState(initialForm);
  const [types, setTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_BASE}/partnership/inquiry-types`)
      .then((r) => setTypes(r.data.types || []))
      .catch(() => setTypes([]));
  }, []);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please write at least a sentence or two so we can help.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/partnership/inquiry`,
        form
      );
      setSubmitted(data);
      toast.success("Inquiry sent. We'll respond within one business day.");
      setForm(initialForm);
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          "Something went wrong. Please email nsarpong@goodwinliving.org directly."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white overflow-x-hidden">
      <SEO
        title="Partnerships — HospiceGuard | Consultants, Resellers & EMR Integrators"
        description="Partner with HospiceGuard. For hospice consultants, resellers, EMR/EHR integrators, and enterprise hospice networks. Reach our founder directly — we respond within one business day."
        path="/partnership"
        keywords="hospice consultant partnership, hospice software reseller, EMR integration partner"
      />
      {/* Ambient background — grid + cyan orb */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-full bg-[#00E5FF]/20 blur-[120px]" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] rounded-full bg-[#3B82F6]/10 blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8 lg:px-12 lg:py-14">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-400 hover:text-[#00E5FF] transition-colors"
          data-testid="partnership-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to HospiceGuard</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-10 mb-14"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] text-xs font-mono border border-[#00E5FF]/20 mb-4">
            <Handshake className="w-3.5 h-3.5" />
            <span className="tracking-[0.15em]">PARTNERSHIPS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
            Let&apos;s build the future of<br />
            <span className="text-[#00E5FF]">hospice compliance</span> together.
          </h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-base leading-relaxed">
            Consultants, resellers, EMR integrators, and enterprise hospice
            networks — this form goes straight to our founder&apos;s inbox. We
            respond within one business day.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left sidebar: direct contact + trust badges */}
          <motion.aside
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-[#0A0A0F]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">
                Prefer direct?
              </div>
              <a
                href="mailto:nsarpong@goodwinliving.org?subject=HospiceGuard%20Partnership%20Inquiry"
                className="flex items-center gap-3 py-2 group"
                data-testid="partnership-direct-email"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <Mail className="w-4 h-4 text-[#00E5FF]" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  <div className="text-sm text-white group-hover:text-[#00E5FF] transition-colors">
                    nsarpong@goodwinliving.org
                  </div>
                </div>
              </a>
              <a
                href="tel:+15713296603"
                className="flex items-center gap-3 py-2 group"
                data-testid="partnership-direct-phone"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <Phone className="w-4 h-4 text-[#00E5FF]" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Phone</div>
                  <div className="text-sm text-white group-hover:text-[#00E5FF] transition-colors">
                    (571) 329-6603
                  </div>
                </div>
              </a>
            </div>

            <div className="bg-[#0A0A0F]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">
                <Shield className="w-3.5 h-3.5" />
                <span>What we look for</span>
              </div>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex gap-2">
                  <span className="text-[#00E5FF] flex-shrink-0">›</span>
                  <span>Deep hospice or home-health domain expertise</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#00E5FF] flex-shrink-0">›</span>
                  <span>Existing relationships with 5+ hospice agencies</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#00E5FF] flex-shrink-0">›</span>
                  <span>Technical partners: EMR/EHR, telehealth, or FHIR API integrations</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#00E5FF] flex-shrink-0">›</span>
                  <span>Investors and enterprise buyers exploring the space</span>
                </li>
              </ul>
            </div>
          </motion.aside>

          {/* Form */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-3"
            data-testid="partnership-form-section"
          >
            {submitted ? (
              <div
                className="bg-[#0A0A0F]/60 backdrop-blur-xl border border-[#00E5FF]/30 rounded-2xl p-8 shadow-[0_0_40px_-10px_rgba(0,229,255,0.15)]"
                data-testid="partnership-success-state"
              >
                <div className="w-14 h-14 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-7 h-7 text-[#00E5FF]" />
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-3">
                  Message received.
                </h2>
                <p className="text-slate-300 mb-2">{submitted.message}</p>
                <p className="text-xs font-mono text-slate-500 mt-4">
                  Inquiry ID:{" "}
                  <span className="text-[#00E5FF]">
                    {submitted.inquiry_id?.slice(0, 8)}
                  </span>
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setSubmitted(null)}
                    className="bg-transparent text-white border border-white/20 hover:border-white/50 px-6 py-3 rounded-full text-sm transition-colors"
                    data-testid="partnership-send-another"
                  >
                    Send another
                  </button>
                  <Link
                    to="/"
                    className="bg-[#00E5FF] text-[#05050A] font-medium px-6 py-3 rounded-full text-sm hover:shadow-[0_0_20px_-5px_rgba(0,229,255,0.4)] transition-shadow inline-flex items-center gap-2"
                    data-testid="partnership-back-to-home"
                  >
                    Back to home
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-[#0A0A0F]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 lg:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Full name"
                    required
                    testid="partnership-input-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Doe"
                    autoComplete="name"
                  />
                  <FormField
                    label="Email"
                    required
                    type="email"
                    testid="partnership-input-email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@company.com"
                    autoComplete="email"
                  />
                  <FormField
                    label="Phone (optional)"
                    testid="partnership-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(555) 555-5555"
                    autoComplete="tel"
                  />
                  <FormField
                    label="Company / Organization"
                    testid="partnership-input-org"
                    value={form.organization}
                    onChange={set("organization")}
                    placeholder="Sunrise Consulting LLC"
                    autoComplete="organization"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-2">
                    Inquiry type
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {(types.length ? types : DEFAULT_TYPES).map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() =>
                          setForm((p) => ({ ...p, inquiry_type: t.value }))
                        }
                        data-testid={`partnership-type-${t.value}`}
                        className={`text-left px-3 py-2.5 rounded-lg text-xs border transition-colors ${
                          form.inquiry_type === t.value
                            ? "border-[#00E5FF]/60 bg-[#00E5FF]/10 text-[#00E5FF]"
                            : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-2">
                    Tell us what you&apos;re thinking <span className="text-[#00E5FF]">*</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={6}
                    placeholder="Which agencies are you working with? What outcome would make this partnership a win for both of us?"
                    data-testid="partnership-input-message"
                    className="w-full bg-white/[0.02] border border-white/10 focus:border-[#00E5FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 rounded-lg px-3.5 py-3 text-sm text-white placeholder-slate-500 resize-y transition-colors"
                  />
                  <div className="mt-1 text-right text-xs text-slate-500 font-mono">
                    {form.message.length}/4000
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="partnership-submit-btn"
                    className="w-full sm:w-auto bg-[#00E5FF] text-[#05050A] font-semibold px-8 py-3.5 rounded-full text-sm hover:shadow-[0_0_25px_-5px_rgba(0,229,255,0.6)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Sending…</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send inquiry</span>
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-slate-500">
                    By submitting you agree we can contact you about your
                    inquiry. We never sell contact info.
                  </p>
                </div>
              </form>
            )}
          </motion.section>
        </div>
      </div>
      <div className="relative z-10"><PublicPageCopy page="partnership" tone="dark" /></div>
    </div>
  );
}

const DEFAULT_TYPES = [
  { value: "consultant", label: "Consultant" },
  { value: "reseller", label: "Reseller" },
  { value: "emr_integration", label: "EMR Integration" },
  { value: "enterprise", label: "Enterprise" },
  { value: "demo_request", label: "Demo Request" },
  { value: "general", label: "General" },
];

function FormField({
  label,
  required,
  type = "text",
  testid,
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-2">
        {label}
        {required && <span className="text-[#00E5FF] ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full bg-white/[0.02] border border-white/10 focus:border-[#00E5FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 transition-colors"
      />
    </div>
  );
}
